import React from "react";
import { Link } from "react-router-dom";
import { Phone, MapPin, ArrowRight, Sparkles, Clock, CheckCircle2 } from "lucide-react";
import QuoteForm from "../QuoteForm";
import HeroBackground from "../HeroBackground";
import { BUSINESS, HERO_TRUST_BADGES } from "../../data/business";

/**
 * Mobile (<sm) -> compact title-only pill row so the section answers
 *   "what do they do?" in a glance without a wall of text.
 * Desktop (>=sm) -> full 3-up card grid with descriptions.
 */
function TrustBadges() {
  return (
    <>
      {/* Mobile-only: compact pills, titles only */}
      <div className="mt-5 flex flex-wrap gap-2 sm:hidden" data-testid="hero-trust-pills-mobile">
        {HERO_TRUST_BADGES.map((b) => (
          <span
            key={b.title}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 backdrop-blur"
            data-testid={`hero-pill-${b.title.toLowerCase().replace(/ /g, "-")}`}
          >
            <CheckCircle2 className="w-3.5 h-3.5 text-[#E31D2B]" />
            <span className="text-[12px] font-semibold text-white">{b.title}</span>
          </span>
        ))}
      </div>

      {/* Desktop / tablet: full cards with descriptions */}
      <div className="mt-6 hidden sm:grid sm:grid-cols-3 gap-3 max-w-2xl">
        {HERO_TRUST_BADGES.map((b) => (
          <div
            key={b.title}
            className="rounded-xl bg-white/8 backdrop-blur border border-white/12 px-4 py-3"
            data-testid={`hero-badge-${b.title.toLowerCase().replace(/ /g, "-")}`}
          >
            <div className="text-[#E31D2B] font-bold text-sm">{b.title}</div>
            <div className="text-xs text-white/75 mt-0.5 leading-snug">{b.desc}</div>
          </div>
        ))}
      </div>
    </>
  );
}

export default function HeroSection() {
  return (
    // `lg:min-h-[calc(100vh-100px)]` — TopBanner (~35.5px) + Header (~65px,
    // h-16 + 1px border) = ~100.5px of chrome at lg+ widths. We undercut by
    // ~0.5px so the hero is flush against the next section with no white
    // sliver. `flex items-center` vertically centers the content within the
    // remaining space.
    <section
      className="relative isolate overflow-hidden lg:min-h-[calc(100vh-100px)] lg:flex lg:items-center"
      data-testid="home-hero"
    >
      <HeroBackground />

      <div className="abc-container w-full py-8 lg:py-12 grid lg:grid-cols-12 gap-6 lg:gap-10 items-center">
        <div className="lg:col-span-7 text-white">
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/15 text-[11px] sm:text-xs font-semibold uppercase tracking-widest"
            data-testid="hero-eyebrow"
          >
            <Sparkles className="w-3.5 h-3.5 text-[#E31D2B]" />
            <span className="sm:hidden">Plumbers · Baltimore, MD</span>
            <span className="hidden sm:inline">Local plumbers · Baltimore, MD</span>
          </div>

          <h1
            className="font-display mt-3 sm:mt-4 text-[28px] leading-[1.1] sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold sm:leading-[1.05] tracking-tight"
            data-testid="hero-h1"
          >
            Drain &amp; Sewer Help in <span className="text-[#E31D2B]">Baltimore, MD</span>
          </h1>

          {/* Short, conversion-first subhead on mobile; fuller value-prop on desktop. */}
          <p className="mt-3 sm:hidden text-[15px] text-white/85 leading-relaxed" data-testid="hero-subhead-mobile">
            Local plumbers clearing drains, inspecting sewer lines, and fixing leaks across Baltimore — clear pricing, clean work.
          </p>
          <p
            className="mt-4 hidden sm:block text-base sm:text-lg text-white/85 max-w-xl leading-relaxed"
            data-testid="hero-subhead"
          >
            Drain Sights handles drain cleaning, sewer camera inspection, hydro-jetting, and sewer line work for homeowners across the Baltimore and DC metros — clear pricing, clean work, done right the first time.
          </p>

          <div className="mt-5 flex flex-wrap gap-3" data-testid="hero-ctas">
            <a
              href={BUSINESS.phoneHref}
              className="abc-btn abc-btn-accent text-base py-3.5 px-5"
              data-testid="hero-call-cta"
            >
              <Phone className="w-5 h-5" /> Call {BUSINESS.phone}
            </a>
            <Link
              to="/contact"
              className="abc-btn abc-btn-ghost-light text-base py-3.5 px-5"
              data-testid="hero-quote-cta"
            >
              Get a Free Quote <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          <TrustBadges />

          {/* Service area + hours strip — full on desktop, condensed on mobile. */}
          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[13px] sm:text-sm text-white/80">
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="w-4 h-4 text-[#54616E]" />
              <span className="sm:hidden">Baltimore &amp; DC metros</span>
              <span className="hidden sm:inline">Baltimore, Glen Burnie, Clinton, Fort Washington &amp; more</span>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="w-4 h-4 text-[#E31D2B]" /> {BUSINESS.hours.weekdays}
            </span>
          </div>
        </div>

        <div className="lg:col-span-5">
          <QuoteForm source="homepage-hero" />
        </div>
      </div>
    </section>
  );
}
